import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

/**
 * Shown once after inviting someone (M4): the link exists only now. It works once, for 7 days, and
 * only for the invited email address. (Sending it by email arrives with the M5 worker.)
 */
export const InviteLinkDialog = ({
  email,
  link,
  onClose,
}: {
  email: string;
  link: string;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const [copied, setCopied] = useState(false);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("crm.workspaces.invitation.title", {
              _: "Invitation created",
            })}
          </DialogTitle>
          <DialogDescription>
            {translate("crm.workspaces.invitation.help", {
              _: "Send this link to %{email}. It works once, for 7 days, and only when signed in with that email address. It won't be shown again.",
              email,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input
            readOnly
            value={link}
            aria-label="Invitation link"
            onFocus={(event) => event.target.select()}
          />
          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              await navigator.clipboard.writeText(link);
              setCopied(true);
            }}
          >
            {copied ? <Check /> : <Copy />}
            {translate(copied ? "crm.common.copied" : "crm.common.copy", {
              _: copied ? "Copied" : "Copy",
            })}
          </Button>
        </div>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {translate("crm.common.done", { _: "Done" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
